import { useState } from 'react'
import { MessageSquare, Plus, Check, X, Trash2, Pencil, CornerDownRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { EmptyState } from '@/components/shared/empty-state'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { cn } from '@/lib/utils/cn'
import dayjs from 'dayjs'
import {
  useRecordCommentsQuery,
  useAddRecordCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useAddReplyMutation,
} from '../api/use-records-query'
import type { CommentRow } from '../api/records-api'

function isWithin5Min(createdAt: string) {
  return dayjs().diff(dayjs(createdAt), 'minute') < 5
}

export function RecordCommentsTab({ recordId }: { recordId: string }) {
  const user = useAuthStore((s) => s.user)
  const { role } = useProjectRole()
  const [composing, setComposing] = useState(false)
  const [body, setBody] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [replyingToId, setReplyingToId] = useState<string | null>(null)
  const [replyBody, setReplyBody] = useState('')

  const { data: allComments = [], isLoading } = useRecordCommentsQuery(recordId)
  const addMutation = useAddRecordCommentMutation(recordId)
  const updateMutation = useUpdateCommentMutation(recordId)
  const deleteMutation = useDeleteCommentMutation(recordId)
  const replyMutation = useAddReplyMutation(recordId)

  const topLevel = allComments.filter((c: CommentRow) => !c.parent_comment_id)
  const replyMap = allComments.reduce<Record<string, CommentRow[]>>((acc, c: CommentRow) => {
    if (c.parent_comment_id) acc[c.parent_comment_id] = [...(acc[c.parent_comment_id] ?? []), c]
    return acc
  }, {})

  const handleAdd = () => {
    if (!body.trim() || !user) return
    addMutation.mutate({ recordId, body, userId: user.id, userRole: role ?? 'viewer' }, {
      onSuccess: () => { setBody(''); setComposing(false) },
    })
  }

  const renderComment = (c: CommentRow, isReply = false) => {
    const isOwn = c.author_id === user?.id
    const canEdit = isOwn && isWithin5Min(c.created_at)
    const replies = replyMap[c.id] ?? []

    return (
      <div key={c.id}>
        <div
          className={cn(
            'bg-card border rounded-xl p-4',
            isReply && 'ms-6 rounded-s-none border-s-2',
            c.origin === 'decline' ? 'border-red-500/30' : 'border-border'
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-[10px] font-medium text-primary">
                  {(c.author?.full_name ?? 'U')[0]}
                </span>
              </div>
              <span className="text-xs font-medium text-foreground">
                {c.author?.full_name ?? 'کاربر'}
              </span>
              {c.origin === 'decline' && (
                <span className="text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">رد شده</span>
              )}
              {c.is_resolved && (
                <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">حل شده</span>
              )}
              {(c as CommentRow & { is_edited?: boolean }).is_edited && (
                <span className="text-[10px] text-muted-foreground">(ویرایش شده)</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">{dayjs(c.created_at).fromNow()}</span>
              {canEdit && (
                <button className="text-muted-foreground hover:text-foreground" onClick={() => { setEditingId(c.id); setEditBody(c.body) }}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
              {isOwn && (
                <button className="text-muted-foreground hover:text-red-400" onClick={() => deleteMutation.mutate({ commentId: c.id, userId: user!.id })} disabled={deleteMutation.isPending}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              {!isReply && (
                <button className="text-muted-foreground hover:text-foreground" onClick={() => setReplyingToId(replyingToId === c.id ? null : c.id)}>
                  <CornerDownRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {editingId === c.id ? (
            <div className="space-y-2">
              <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2} className="text-sm resize-none" autoFocus />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                  <X className="w-4 h-4 me-1" />انصراف
                </Button>
                <Button size="sm" disabled={!editBody.trim() || updateMutation.isPending}
                  onClick={() => updateMutation.mutate({ commentId: c.id, body: editBody }, { onSuccess: () => setEditingId(null) })}>
                  <Check className="w-4 h-4 me-1" />ذخیره
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground leading-relaxed">{c.body}</p>
          )}
        </div>

        {/* Replies */}
        {replies.map((r) => renderComment(r, true))}

        {/* Reply compose */}
        {replyingToId === c.id && (
          <div className="ms-6 mt-1 bg-card border border-border rounded-xl p-3 space-y-2">
            <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="پاسخ..." rows={2} className="text-sm resize-none" autoFocus />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => { setReplyingToId(null); setReplyBody('') }}>
                <X className="w-4 h-4 me-1" />انصراف
              </Button>
              <Button size="sm" disabled={!replyBody.trim() || replyMutation.isPending}
                onClick={() => replyMutation.mutate(
                  { recordId, parentCommentId: c.id, body: replyBody, userId: user!.id, userRole: role ?? 'viewer', scope: 'record' },
                  { onSuccess: () => { setReplyingToId(null); setReplyBody('') } }
                )}>
                <Check className="w-4 h-4 me-1" />ارسال
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">
          نظرات ({topLevel.length.toLocaleString('fa-IR')})
        </h3>
        {!composing && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setComposing(true)}>
            <Plus className="w-4 h-4" />
            افزودن نظر
          </Button>
        )}
      </div>

      {composing && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="نظر خود را بنویسید..." rows={3} autoFocus />
          <div className="flex items-center gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setComposing(false); setBody('') }}>
              <X className="w-4 h-4 me-1" />انصراف
            </Button>
            <Button size="sm" disabled={!body.trim() || addMutation.isPending} onClick={handleAdd}>
              <Check className="w-4 h-4 me-1" />ثبت نظر
            </Button>
          </div>
        </div>
      )}

      {topLevel.length === 0 && !composing ? (
        <EmptyState icon={MessageSquare} title="نظری ثبت نشده" description="اولین نظر را برای این سابقه ثبت کنید" />
      ) : (
        <div className="space-y-3">
          {topLevel.map((c: CommentRow) => renderComment(c))}
        </div>
      )}
    </div>
  )
}
